import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useApp } from '@/context/AppContext';
import { Button } from '@/components/ui/button';
import {
  Globe, LogOut, User, Menu, X, Sparkles, BookOpen, Library, Calendar,
  ClipboardCheck, MessageCircle, Languages, Shield, Users, Building2,
  BarChart3, Award, Target, Home, GraduationCap, FileText, Activity, Trophy, ChevronDown, Film,
} from 'lucide-react';

// ============================================================
// Role-based navigation registry
//   - Each role has a dedicated set of links + a primary dashboard.
//   - Links flagged with group:'platform' are collected into the
//     "منصة الرياضة الملائمة" (Adapted Sports Platform) dropdown.
// ============================================================
function buildLinks(user, lang) {
  const t = (ar, en) => (lang === 'ar' ? ar : en);
  if (!user) {
    return [
      { to: '/', label: t('الرئيسية', 'Home'), testid: 'nav-home' },
      { to: '/curriculum', label: t('المنهج الدراسي', 'Curriculum'), testid: 'nav-curriculum', icon: BookOpen, group: 'platform' },
    ];
  }
  switch (user.role) {
    case 'admin':
      return [
        { to: '/admin', label: t('لوحة الإدارة', 'Admin'), testid: 'nav-dashboard', icon: Shield, primary: true, group: 'platform' },
        { to: '/curriculum', label: t('المنهج', 'Curriculum'), testid: 'nav-curriculum', icon: BookOpen, group: 'platform' },
        { to: '/admin/users', label: t('المستخدمون', 'Users'), testid: 'nav-admin-users', icon: Users },
        { to: '/admin/centers', label: t('المراكز', 'Centers'), testid: 'nav-admin-centers', icon: Building2 },
        { to: '/admin/analytics', label: t('التحليلات', 'Analytics'), testid: 'nav-admin-analytics', icon: BarChart3 },
        { to: '/exercises', label: t('مكتبة التمارين', 'Exercises'), testid: 'nav-exercises', icon: Activity },
        { to: '/admin/translations', label: t('الترجمات', 'Translations'), testid: 'nav-translations', icon: Languages },
        { to: '/admin/audit', label: t('سجلّ النظام', 'Audit log'), testid: 'nav-admin-audit', icon: FileText },
      ];
    case 'teacher':
      return [
        { to: '/teacher', label: t('لوحة المعلم', 'Teacher dashboard'), testid: 'nav-dashboard', icon: GraduationCap, primary: true, group: 'platform' },
        { to: '/curriculum', label: t('المنهج', 'Curriculum'), testid: 'nav-curriculum', icon: BookOpen, group: 'platform' },
        { to: '/student-plans', label: t('الخطط الفردية', 'IEPs'), testid: 'nav-student-plans', icon: ClipboardCheck, group: 'platform' },
        { to: '/plans', label: t('الخطط', 'Plans'), testid: 'nav-plans', icon: Calendar, group: 'platform' },
        { to: '/rubrics', label: t('الروبركس', 'Rubrics'), testid: 'nav-rubrics', icon: ClipboardCheck, group: 'platform' },
        { to: '/exercises', label: t('مكتبة التمارين', 'Exercises'), testid: 'nav-exercises', icon: Activity, highlight: true },
        { to: '/ai-studio', label: t('استوديو AI', 'AI Studio'), testid: 'nav-ai-studio', icon: Sparkles },
        { to: '/video-library', label: t('مكتبة الفيديو', 'Video Library'), testid: 'nav-video-library', icon: Film },
        { to: '/messages', label: t('الرسائل', 'Messages'), testid: 'nav-messages', icon: MessageCircle },
      ];
    case 'parent':
      return [
        { to: '/parent', label: t('لوحة ولي الأمر', 'My family'), testid: 'nav-dashboard', icon: Home, primary: true, group: 'platform' },
        { to: '/curriculum', label: t('المنهج', 'Curriculum'), testid: 'nav-curriculum', icon: BookOpen, group: 'platform' },
        { to: '/messages', label: t('مراسلة المعلم', 'Messages'), testid: 'nav-messages', icon: MessageCircle },
        { to: '/exercises', label: t('التمارين', 'Exercises'), testid: 'nav-exercises', icon: Activity },
        { to: '/video-library', label: t('مكتبة الفيديو', 'Video Library'), testid: 'nav-video-library', icon: Film },
        { to: '/ai-studio', label: t('استوديو AI', 'AI Studio'), testid: 'nav-ai-studio', icon: Sparkles, highlight: true },
      ];
    case 'student':
      return [
        { to: '/student', label: t('رحلتي', 'My journey'), testid: 'nav-dashboard', icon: Award, primary: true, group: 'platform' },
        { to: '/curriculum', label: t('المنهج', 'Curriculum'), testid: 'nav-curriculum', icon: BookOpen, group: 'platform' },
        { to: '/exercises', label: t('التمارين', 'Exercises'), testid: 'nav-exercises', icon: Activity },
        { to: '/video-library', label: t('مكتبة الفيديو', 'Video Library'), testid: 'nav-video-library', icon: Film },
        { to: '/messages', label: t('الرسائل', 'Messages'), testid: 'nav-messages', icon: MessageCircle },
      ];
    default:
      return [{ to: '/', label: t('الرئيسية', 'Home'), testid: 'nav-home' }];
  }
}

const ROLE_BADGE = {
  admin: { ar: 'مسؤول', en: 'Admin', color: 'bg-rose-100 text-rose-700 border-rose-200' },
  teacher: { ar: 'معلم', en: 'Teacher', color: 'bg-primary/10 text-primary border-primary/20' },
  parent: { ar: 'ولي أمر', en: 'Parent', color: 'bg-accent/15 text-accent-foreground border-accent/30' },
  student: { ar: 'طالب', en: 'Student', color: 'bg-purple-100 text-purple-700 border-purple-200' },
};

// Desktop dropdown for "منصة الرياضة الملائمة" — collects all platform links (dashboard, curriculum,
// individual plans, plans, rubrics) into a single grouped menu.
function PlatformMenu({ lang, platformLinks, isActive }) {
  const [open, setOpen] = useState(false);
  const close = () => setOpen(false);
  if (!platformLinks.length) return null;
  const anyActive = platformLinks.some((l) => isActive(l.to));
  return (
    <div className="relative" onMouseLeave={close}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        onMouseEnter={() => setOpen(true)}
        className={`px-3 py-2 rounded-lg text-sm font-semibold inline-flex items-center gap-1.5 transition-colors ${anyActive ? 'text-primary bg-primary/10' : 'text-foreground/85 hover:bg-secondary'}`}
        aria-haspopup="menu"
        aria-expanded={open}
        data-testid="nav-platform"
      >
        <GraduationCap className="w-3.5 h-3.5" aria-hidden="true" />
        {lang === 'ar' ? 'منصة الرياضة الملائمة' : 'Adapted Sports Platform'}
        <ChevronDown className={`w-3 h-3 transition-transform ${open ? 'rotate-180' : ''}`} aria-hidden="true" />
      </button>
      {open && (
        <div
          role="menu"
          className="absolute top-full start-0 mt-1 w-72 bg-white border-2 border-border rounded-xl shadow-xl p-2 z-50 max-h-[80vh] overflow-y-auto"
          data-testid="platform-menu"
        >
          {platformLinks.map((l, idx) => {
            const Icon = l.icon;
            const active = isActive(l.to);
            return (
              <React.Fragment key={l.to}>
                {idx === 1 && <div className="border-t my-1" />}
                <Link
                  to={l.to}
                  onClick={close}
                  role="menuitem"
                  data-testid={l.testid}
                  className={`flex items-start gap-3 p-3 rounded-lg transition-colors ${active ? 'bg-primary/10' : 'hover:bg-secondary'}`}
                >
                  <div className={`w-9 h-9 rounded-lg grid place-items-center shrink-0 ${l.primary ? 'bg-primary/15 text-primary' : 'bg-secondary text-foreground/80'}`}>
                    {Icon && <Icon className="w-5 h-5" aria-hidden="true" />}
                  </div>
                  <div className="flex-1">
                    <div className={`font-bold text-sm ${l.primary ? 'text-primary' : ''}`}>{l.label}</div>
                    {l.primary && <div className="text-[10px] text-muted-foreground">{lang === 'ar' ? 'لوحة التحكم الرئيسية' : 'Main dashboard'}</div>}
                  </div>
                </Link>
              </React.Fragment>
            );
          })}
        </div>
      )}
    </div>
  );
}

// Desktop dropdown for "Academies / الأكاديميات" — keyboard- and click-friendly.
function AcademiesMenu({ lang }) {
  const [open, setOpen] = useState(false);
  const close = () => setOpen(false);
  return (
    <div className="relative" onMouseLeave={close}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        onMouseEnter={() => setOpen(true)}
        className="px-3 py-2 rounded-lg text-sm font-semibold inline-flex items-center gap-1.5 text-foreground/85 hover:bg-secondary"
        aria-haspopup="menu"
        aria-expanded={open}
        data-testid="nav-academies"
      >
        <Trophy className="w-3.5 h-3.5" aria-hidden="true" />
        {lang === 'ar' ? 'الأكاديميات' : 'Academies'}
        <ChevronDown className={`w-3 h-3 transition-transform ${open ? 'rotate-180' : ''}`} aria-hidden="true" />
      </button>
      {open && (
        <div
          role="menu"
          className="absolute top-full start-0 mt-1 w-80 bg-white border-2 border-border rounded-xl shadow-xl p-2 z-50 max-h-[80vh] overflow-y-auto"
          data-testid="academies-menu"
        >
          <Link to="/academy/paralympic" onClick={close} role="menuitem" data-testid="nav-academy-paralympic" className="flex items-start gap-3 p-3 rounded-lg hover:bg-cyan-50">
            <div className="w-9 h-9 rounded-lg bg-cyan-100 grid place-items-center shrink-0"><Trophy className="w-5 h-5 text-cyan-700" /></div>
            <div>
              <div className="font-bold text-sm">{lang === 'ar' ? 'الأكاديمية البارالمبية' : 'Paralympic Academy'}</div>
              <div className="text-[11px] text-muted-foreground line-clamp-2">{lang === 'ar' ? 'تدريب وتصنيف للرياضات البارالمبية' : 'Training & classification for Para sports'}</div>
            </div>
          </Link>
          <Link to="/academy/special-olympics" onClick={close} role="menuitem" data-testid="nav-academy-special" className="flex items-start gap-3 p-3 rounded-lg hover:bg-violet-50 mt-1">
            <div className="w-9 h-9 rounded-lg bg-violet-100 grid place-items-center shrink-0"><Award className="w-5 h-5 text-violet-700" /></div>
            <div>
              <div className="font-bold text-sm">{lang === 'ar' ? 'الأولمبياد الخاص' : 'Special Olympics'}</div>
              <div className="text-[11px] text-muted-foreground line-clamp-2">{lang === 'ar' ? 'أكاديمية ذكية لذوي الإعاقة الذهنية' : 'Smart academy for intellectual disabilities'}</div>
            </div>
          </Link>
          <div className="border-t my-1" />
          <Link to="/academy/talent-assessment" onClick={close} role="menuitem" data-testid="nav-academy-talent" className="flex items-start gap-3 p-3 rounded-lg hover:bg-amber-50">
            <div className="w-9 h-9 rounded-lg bg-amber-100 grid place-items-center shrink-0"><Sparkles className="w-5 h-5 text-amber-700" /></div>
            <div>
              <div className="font-bold text-sm">{lang === 'ar' ? 'تقييم المواهب' : 'Talent Assessment'}</div>
              <div className="text-[11px] text-muted-foreground line-clamp-2">{lang === 'ar' ? 'اكتشف الرياضة الأنسب لك' : 'Discover your perfect sport'}</div>
            </div>
          </Link>
          <Link to="/academy/training" onClick={close} role="menuitem" data-testid="nav-academy-training" className="flex items-start gap-3 p-3 rounded-lg hover:bg-emerald-50">
            <div className="w-9 h-9 rounded-lg bg-emerald-100 grid place-items-center shrink-0"><Target className="w-5 h-5 text-emerald-700" /></div>
            <div>
              <div className="font-bold text-sm">{lang === 'ar' ? 'مناهج التدريب الذكية' : 'Smart Training Curricula'}</div>
              <div className="text-[11px] text-muted-foreground line-clamp-2">{lang === 'ar' ? 'مكتبة + مولّد AI شخصي' : 'Library + personal AI generator'}</div>
            </div>
          </Link>
          <Link to="/academy/classification" onClick={close} role="menuitem" data-testid="nav-academy-classify" className="flex items-start gap-3 p-3 rounded-lg hover:bg-sky-50">
            <div className="w-9 h-9 rounded-lg bg-sky-100 grid place-items-center shrink-0"><ClipboardCheck className="w-5 h-5 text-sky-700" /></div>
            <div>
              <div className="font-bold text-sm">{lang === 'ar' ? 'التصنيف الفني البارالمبي' : 'Paralympic Classification'}</div>
              <div className="text-[11px] text-muted-foreground line-clamp-2">{lang === 'ar' ? 'تحليل حركي بالذكاء الاصطناعي' : 'AI motion analysis'}</div>
            </div>
          </Link>
        </div>
      )}
    </div>
  );
}

export default function Header() {
  const { t, lang, setLang, user, logout } = useApp();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);

  const links = buildLinks(user, lang);
  const platformLinks = links.filter((l) => l.group === 'platform');
  const inlineLinks = links.filter((l) => l.group !== 'platform');
  const roleBadge = user ? ROLE_BADGE[user.role] : null;

  const isActive = (to) => to === '/' ? location.pathname === '/' : location.pathname === to || (to.length > 1 && location.pathname.startsWith(to + '/'));

  return (
    <header className="sticky top-0 z-50 glass border-b border-border" data-testid="site-header">
      <a href="#main" className="skip-link" data-testid="skip-link">{lang === 'ar' ? 'تخطى إلى المحتوى' : 'Skip to content'}</a>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center gap-3">
        <Link to="/" className="flex items-center gap-3 shrink-0 group" data-testid="brand-link">
          <div className="relative w-10 h-10 rounded-xl bg-gradient-to-br from-primary to-primary/80 text-primary-foreground grid place-items-center font-black text-lg shadow-md group-hover:shadow-lg transition-shadow">
            ه
            <span className="absolute -top-1 -end-1 w-3 h-3 rounded-full bg-accent border-2 border-white" aria-hidden="true" />
          </div>
          <div className="leading-tight hidden sm:block">
            <div className="font-extrabold text-sm">{t('brand')}</div>
            <div className="text-[10px] text-muted-foreground font-medium">{t('brand_sub')}</div>
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-1 ms-2" aria-label="Main">
          <PlatformMenu lang={lang} platformLinks={platformLinks} isActive={isActive} />
          <AcademiesMenu lang={lang} />
          {inlineLinks.map(l => {
            const Icon = l.icon;
            const active = isActive(l.to);
            return (
              <Link
                key={l.to}
                to={l.to}
                data-testid={l.testid}
                className={`relative px-3 py-2 rounded-lg text-sm font-semibold transition-colors inline-flex items-center gap-1.5 ${
                  active ? 'text-primary bg-primary/10' :
                  l.highlight ? 'text-primary hover:bg-primary/5' :
                  l.primary ? 'text-foreground/95 font-bold hover:bg-secondary' :
                  'text-foreground/80 hover:bg-secondary'
                }`}
              >
                {Icon && <Icon className="w-3.5 h-3.5" aria-hidden="true" />}
                {l.label}
                {l.highlight && !active && <span className="ms-0.5 w-1.5 h-1.5 rounded-full bg-accent animate-pulse" aria-hidden="true" />}
              </Link>
            );
          })}
        </nav>

        <div className="ms-auto flex items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setLang(lang === 'ar' ? 'en' : 'ar')}
            data-testid="lang-toggle"
            aria-label={t('lang_switch')}
            className="h-9 font-semibold"
          >
            <Globe className="w-4 h-4 me-2" aria-hidden="true" />
            <span className="hidden sm:inline">{t('lang_switch')}</span>
          </Button>

          {user ? (
            <div className="flex items-center gap-2">
              <span className={`hidden md:inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider border rounded-full px-2 py-1 ${roleBadge?.color}`} data-testid="role-badge">
                {lang === 'ar' ? roleBadge?.ar : roleBadge?.en}
              </span>
              <span className="hidden md:inline-flex items-center gap-2 text-xs font-medium text-muted-foreground bg-secondary px-3 py-1.5 rounded-full">
                <User className="w-3.5 h-3.5" aria-hidden="true" />
                {user.full_name}
              </span>
              <Button variant="outline" size="sm" onClick={() => { logout(); navigate('/'); }} data-testid="logout-btn" className="h-9">
                <LogOut className="w-4 h-4 me-1" aria-hidden="true" /> <span className="hidden sm:inline">{t('nav_logout')}</span>
              </Button>
            </div>
          ) : (
            <>
              <Button variant="ghost" size="sm" onClick={() => navigate('/login')} data-testid="login-btn" className="h-9">{t('nav_login')}</Button>
              <Button size="sm" onClick={() => navigate('/register')} data-testid="register-btn" className="bg-primary hover:bg-primary/90 h-9 shadow-sm">{t('nav_register')}</Button>
            </>
          )}

          <button
            className="lg:hidden p-2 rounded-lg hover:bg-secondary"
            onClick={() => setMobileOpen(!mobileOpen)}
            aria-label="Toggle menu"
            aria-expanded={mobileOpen}
            data-testid="mobile-menu-toggle"
          >
            {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {/* Mobile nav */}
      {mobileOpen && (
        <nav className="lg:hidden border-t border-border bg-white/95 backdrop-blur-sm px-4 py-3 space-y-1" aria-label="Mobile" data-testid="mobile-nav">
          {user && roleBadge && (
            <div className={`inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider border rounded-full px-2 py-1 mb-2 ${roleBadge.color}`}>
              {lang === 'ar' ? roleBadge.ar : roleBadge.en} · {user.full_name}
            </div>
          )}
          {/* Academies (mobile) */}
          <div className="border-b border-border pb-2 mb-1" data-testid="mobile-academies-section">
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold px-3 mb-1">{lang === 'ar' ? 'الأكاديميات' : 'Academies'}</div>
            <Link to="/academy/paralympic" onClick={() => setMobileOpen(false)} data-testid="mobile-nav-paralympic" className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-semibold hover:bg-secondary">
              <Trophy className="w-4 h-4 text-cyan-700" />{lang === 'ar' ? 'الأكاديمية البارالمبية' : 'Paralympic Academy'}
            </Link>
            <Link to="/academy/special-olympics" onClick={() => setMobileOpen(false)} data-testid="mobile-nav-special" className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-semibold hover:bg-secondary">
              <Award className="w-4 h-4 text-violet-700" />{lang === 'ar' ? 'الأولمبياد الخاص' : 'Special Olympics'}
            </Link>
            <Link to="/academy/talent-assessment" onClick={() => setMobileOpen(false)} data-testid="mobile-nav-talent" className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-semibold hover:bg-secondary">
              <Sparkles className="w-4 h-4 text-amber-700" />{lang === 'ar' ? 'تقييم المواهب' : 'Talent Assessment'}
            </Link>
            <Link to="/academy/training" onClick={() => setMobileOpen(false)} data-testid="mobile-nav-training" className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-semibold hover:bg-secondary">
              <Target className="w-4 h-4 text-emerald-700" />{lang === 'ar' ? 'مناهج التدريب الذكية' : 'Smart Training'}
            </Link>
            <Link to="/academy/classification" onClick={() => setMobileOpen(false)} data-testid="mobile-nav-classify" className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-semibold hover:bg-secondary">
              <ClipboardCheck className="w-4 h-4 text-sky-700" />{lang === 'ar' ? 'التصنيف الفني' : 'Classification'}
            </Link>
          </div>
          {/* Platform group (mobile) */}
          {platformLinks.length > 0 && (
            <div className="border-b border-border pb-2 mb-1" data-testid="mobile-platform-section">
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold px-3 mb-1">{lang === 'ar' ? 'منصة الرياضة الملائمة' : 'Adapted Sports Platform'}</div>
              {platformLinks.map(l => {
                const Icon = l.icon;
                const active = isActive(l.to);
                return (
                  <Link
                    key={l.to}
                    to={l.to}
                    onClick={() => setMobileOpen(false)}
                    data-testid={`mobile-${l.testid}`}
                    className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-semibold ${active ? 'bg-primary/10 text-primary' : 'hover:bg-secondary'}`}
                  >
                    {Icon && <Icon className="w-4 h-4" aria-hidden="true" />}
                    {l.label}
                  </Link>
                );
              })}
            </div>
          )}
          {inlineLinks.map(l => {
            const Icon = l.icon;
            const active = isActive(l.to);
            return (
              <Link
                key={l.to}
                to={l.to}
                onClick={() => setMobileOpen(false)}
                data-testid={`mobile-${l.testid}`}
                className={`flex items-center gap-2 px-3 py-2.5 rounded-lg text-sm font-semibold ${active ? 'bg-primary/10 text-primary' : 'hover:bg-secondary'}`}
              >
                {Icon && <Icon className="w-4 h-4" aria-hidden="true" />}
                {l.label}
              </Link>
            );
          })}
        </nav>
      )}
    </header>
  );
}
